import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Input,
  Button,
} from "reactstrap";
import { Pie, Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from "chart.js";
import {
  getAPIURL,
  getVendorID,
  getFormattedPrice,
} from "../../services/helper";
import http from "../../services/http";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const TransactionType = () => {
  const api_url = getAPIURL();
  const vendorId = getVendorID();
  const [data, setData] = useState({ summary: {}, transaction_breakdown: {} });
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1))
      .toISOString()
      .split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchTransactionType();
  }, []);

  const fetchTransactionType = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        vendor_id: vendorId,
        start_date: dateRange.start,
        end_date: dateRange.end,
      });
      const { data } = await http.get(
        `${api_url}/reports/transactions/type?${params.toString()}`
      );
      setData(data);
    } catch (error) {
      console.error("Error fetching transaction type report:", error);
    }
    setLoading(false);
  };

  const pieChartData = {
    labels: ['Completed', 'Processing', 'Refunded'],
    datasets: [{
      data: [
        data.transaction_breakdown?.completed_orders || 0,
        data.transaction_breakdown?.processing_orders || 0,
        data.transaction_breakdown?.refunded_orders || 0
      ],
      backgroundColor: ['#28a745', '#ffc107', '#dc3545'],
    }]
  };

  const barChartData = {
    labels: ['Sales', 'Returns', 'Tax', 'Discounts'],
    datasets: [{
      label: 'Amount',
      data: [
        data.summary?.sales?.total_sales_amount || 0,
        data.summary?.returns?.total_returns_amount || 0,
        data.summary?.tax?.total_tax_amount || 0,
        data.summary?.discounts?.total_discount_amount || 0
      ],
      backgroundColor: ['#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0'],
    }]
  };

  return (
    <div className="page-body">
      <Container className="mt-4">
        <h2 className="text-center mb-4">Transaction Type Report</h2>

        {/* Date Range Filter */}
        <Card className="mb-4">
          <CardBody>
            <Row>
              <Col md={4}>
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, start: e.target.value })
                  }
                />
              </Col>
              <Col md={4}>
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, end: e.target.value })
                  }
                />
              </Col>
              <Col md={4}>
                <Button
                  color="primary"
                  onClick={fetchTransactionType}
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Fetch Report"}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Sales Summary */}
        {data.summary?.sales && (
          <Row className="mb-4">
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <h5>Total Sales</h5>
                  <h3>{data.summary.sales.total_sales_count}</h3>
                  <small>{getFormattedPrice(data.summary.sales.total_sales_amount)}</small>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <h5>Returns</h5>
                  <h3>{data.summary.returns?.total_returns_count || 0}</h3>
                  <small>{getFormattedPrice(data.summary.returns?.total_returns_amount || 0)}</small>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <h5>Net Revenue</h5>
                  <h3>{getFormattedPrice(data.summary.financial_summary?.net_revenue || 0)}</h3>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <h5>Total Orders</h5>
                  <h3>{data.total_orders_processed || 0}</h3>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Financial Summary */}
        {data.summary?.financial_summary && (
          <Row className="mb-4">
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <h5>Gross Revenue</h5>
                  <h3>{getFormattedPrice(data.summary.financial_summary.gross_revenue)}</h3>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <h5>Total Tax</h5>
                  <h3>{getFormattedPrice(data.summary.tax?.total_tax_amount || 0)}</h3>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <h5>Total Paid</h5>
                  <h3>{getFormattedPrice(data.summary.payments?.total_paid_amount || 0)}</h3>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <h5>Change Given</h5>
                  <h3>{getFormattedPrice(data.summary.payments?.total_change_given || 0)}</h3>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Charts */}
        <Row className="mb-4">
          <Col md={6}>
            <Card>
              <CardBody>
                <CardTitle tag="h5">Transaction Status</CardTitle>
                <Pie data={pieChartData} />
              </CardBody>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <CardBody>
                <CardTitle tag="h5">Financial Breakdown</CardTitle>
                <Bar data={barChartData} />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default TransactionType;